

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const ItineraryDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [itinerary, setItinerary] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchItineraryData();
    }, [id]);

    const fetchItineraryData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/itineraries/${id}`);
            setItinerary(response.data);
        } catch (error) {
            console.error('Error fetching itinerary details:', error);
            setSnackbar({ open: true, message: 'Error loading itinerary details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setItinerary(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/itineraries/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Itinerary updated successfully', severity: 'success' });
            fetchItineraryData();
        } catch (error) {
            console.error('Error updating itinerary:', error);
            setSnackbar({ open: true, message: 'Error updating itinerary', severity: 'error' });
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({ open: true, message: `Unable to find user info for ${userName}.`, severity: 'info' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this itinerary?')) {
            try {
                await axiosInstance.delete(`/itineraries/${id}`);
                setSnackbar({ open: true, message: 'Itinerary deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/itineraries'), 1000);
            } catch (error) {
                console.error('Error deleting itinerary:', error);
                setSnackbar({ open: true, message: 'Error deleting itinerary', severity: 'error' });
            }
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} sm={6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => handleUserClick(item.field === 'created_by_name' ? itinerary.created_by : itinerary.updated_by, itinerary[item.field])}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {itinerary[item.field]}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={itinerary?.[item.field]}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                renderValue={item.renderValue}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox mb={3} p={3} borderRadius="lg" sx={{ backgroundColor: '#ffffff', boxShadow: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><Skeleton variant="rectangular" height={400} /></MDBox>
                
            </DashboardLayout>
        );
    }

    if (!itinerary) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><MDTypography>Itinerary not found</MDTypography></MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">{itinerary.name}</MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>Itinerary Details</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchItineraryData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Itinerary">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Itinerary">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Name", field: "name" },
                                    { label: "Trip", field: "trip_id", readOnly: true, renderValue: (val) => itinerary.trip?.name || val },
                                    { label: "Total Days", field: "total_days" },
                                    { label: "Title", field: "title" },
                                    { label: "Type", field: "type" },
                                    { label: "Trip Option", field: "trip_option_id", readOnly: true, renderValue: (val) => itinerary.trip_option?.name || val },
                                    { label: "Location", field: "location" },
                                    { label: "Primary Attractions", field: "primary_attractions" },
                                ])}

                                {renderSection("Content Information", [
                                    { label: "Description", field: "description" },
                                    { label: "Image URL", field: "image_url" },
                                    { label: "Meals Summary", field: "meals_summary" },
                                    { label: "Travefy URL", field: "travefy_url" },
                                    { label: "Travefy Sync", field: "travefy_sync", renderValue: (val) => val ? 'Yes' : 'No' },
                                ])}

                                {itinerary.type === 'Influencer' && itinerary.influencer_account_id && (
                                    renderSection("Influencer Information", [
                                        { label: "Account (Influencer)", field: "influencer_account_id", readOnly: true, renderValue: (val) => itinerary.influencer_account?.name || val },
                                    ])
                                )}

                                {renderSection("Audit Information", [
                                    { label: "Created At", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val) },
                                    { label: "Updated At", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val) },
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Updated By", field: "updated_by_name", readOnly: true },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            
        </DashboardLayout>
    );
};

export default ItineraryDetail;

